import {useRouter} from 'next/router';

import {getQueryParam} from '@/utils/helpers/url';

/**
 * @description hook to get the value of an A/B test query parameter
 * that can be set in the URL as a query parameter.
 * @example
 * const abTest = useAbTest<'a'| 'b'>('hpt');
 * if (abTest === null) {
 *    console.log('No A/B test');
 * } else if (abTest === 'a') {
 *    console.log('A/B test A');
 * } else if (abTest === 'b') {
 *    console.log('A/B test B');
 * }
 *
 * @param {string} queryParamName expected A/B test query parameter name
 * @return {string | null} A/B test query parameter value or null if not present
 */
const useAbTest = <Q extends string>(queryParamName: string) => {
  const router = useRouter();

  /**
   * @description `router.query[queryParamName]` will not be present on Staging and Production environments
   * so we can try to use `router.asPath` to get the query params.
   */
  const abTest = getQueryParam({
    pathname: router.asPath,
    key: queryParamName,
  }) as Q | null;

  return abTest;
};

export default useAbTest;
